// src/components/ui/layout/UniversalHeader/UniversalHeader.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Plus,
  Menu,
  Bell,
  User,
  Calendar,
  Users,
  FileText,
  Building2,
  LogOut,
  Settings,
  Mail,
  Phone,
  ArrowLeft,
  Check,
  Loader2
} from 'lucide-react';

// shadcn components
import { Button } from '../../button';
import { Input } from '../../input';
import { Separator } from '../../separator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../form';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../../avatar';

// Sidebar integration
import { useSidebar } from '../../sidebar';

// Auth context
import { useAuth } from '../../../../contexts/AuthContext';

// Utility
import { cn } from '../../../../utils/cn';

// Customer hook for creation
import { useCustomers } from '../../../../hooks/useCustomers';
import { useToast } from '../../../../hooks/use-toast';

// Header notification system
import HeaderNotification from '../../../notifications/HeaderNotification';
import useNotificationStore from '../../../../stores/NotificationStore';

// Customer form validation schema
const customerSchema = z.object({
  name: z
    .string()
    .min(1, 'Customer name is required')
    .max(100, 'Name cannot exceed 100 characters')
    .transform(val => val.trim()),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .transform(val => val.trim().toLowerCase()),
  phone: z
    .string()
    .min(1, 'Phone number is required')
    .refine(val => {
      const digitsOnly = val.replace(/\D/g, '');
      return digitsOnly.length >= 10 && digitsOnly.length <= 15;
    }, 'Phone number must be between 10-15 digits')
    .transform(val => val.trim())
});

const UniversalHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleSidebar } = useSidebar();
  const { currentUser, logout, allBusinesses, switchBusiness, switchingBusiness } = useAuth();
  const { createCustomer, error: customerError } = useCustomers();
  const { toast } = useToast();

  const [dropdownMode, setDropdownMode] = useState('menu'); // 'menu' or 'customerForm'
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Header notification state - get the first notification from store
  const notifications = useNotificationStore(state => state.notifications);
  const removeNotification = useNotificationStore(state => state.remove);

  // Get first notification from store (they all go to header now)
  const storeNotification = notifications[0]; // First notification in queue

  // Keep track of the displayed notification to prevent unmounting during exit animation
  const [displayedNotification, setDisplayedNotification] = useState(null);

  // Update displayed notification when a new one arrives
  useEffect(() => {
    if (storeNotification) {
      setDisplayedNotification(storeNotification);
    }
  }, [storeNotification]);

  // Handle notification dismissal
  const handleNotificationDismiss = (id) => {
    // Remove from store
    removeNotification(id);
    // Clear displayed notification after a delay to allow exit animation
    setTimeout(() => {
      setDisplayedNotification(null);
    }, 400);
  };

  // Customer form setup
  const customerForm = useForm({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: ''
    }
  });

  // Generate breadcrumbs from current route
  const generateBreadcrumbs = () => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);

    // Base breadcrumb for root
    const breadcrumbs = [
      { label: 'Dashboard', href: '/', isCurrent: path === '/' }
    ];

    // Map route segments to readable names
    const routeMap = {
      'customers': 'Customers',
      'invoices': 'Invoices',
      'appointments': 'Appointments',
      'calendar': 'Calendar',
      'partners': 'Partners',
      'widgets': 'Website Integration',
      'configuration': 'Configuration',
      'business': 'Business Settings',
      'communications': 'Communications',
      'services': 'Services',
      'team': 'Team',
      'create': 'Create',
      'edit': 'Edit'
    };

    // Build breadcrumb path
    let currentPath = '';
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === segments.length - 1;
      const label = routeMap[segment] || segment;

      breadcrumbs.push({
        label,
        href: currentPath,
        isCurrent: isLast
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Global action handlers
  const handleNewAppointment = () => navigate('/calendar');
  const handleCreateInvoice = () => navigate('/invoices/create');
  const handleAddCustomer = () => {
    setDropdownMode('customerForm');
  };

  // Customer form handlers
  const handleCustomerSubmit = async (customerData) => {
    try {
      setIsSubmitting(true);

      console.log('🆕 Creating customer from header:', customerData);

      createCustomer(customerData);

      toast({
        title: "Success!",
        description: `Customer "${customerData.name}" created successfully!`,
      });

      // Reset form and return to menu
      customerForm.reset();
      setDropdownMode('menu');

    } catch (err) {
      console.error('❌ Customer creation error:', err);
      toast({
        title: "Error",
        description: err.message || 'Failed to create customer. Please try again.',
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToMenu = () => {
    customerForm.reset();
    setDropdownMode('menu');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    const initials = names.map(n => n.charAt(0).toUpperCase()).join('');
    return initials.slice(0, 2);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-700/50 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 backdrop-blur-xl shadow-lg shadow-black/10">
      <div className="flex h-14 items-center gap-4 px-4">

        {/* Mobile sidebar toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 md:hidden"
          onClick={toggleSidebar}
        >
          <Menu className="h-4 w-4" />
          <span className="sr-only">Toggle sidebar</span>
        </Button>

        {/* Separator */}
        <Separator orientation="vertical" className="h-4 hidden md:block" />

        {/* Breadcrumbs */}
        <Breadcrumb className="hidden sm:flex">
          <BreadcrumbList>
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={breadcrumb.href}>
                <BreadcrumbItem>
                  {breadcrumb.isCurrent ? (
                    <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink
                      href={breadcrumb.href}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(breadcrumb.href);
                      }}
                    >
                      {breadcrumb.label}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header Notification Slot - slides left to right */}
        {displayedNotification && (
          <HeaderNotification
            notification={displayedNotification}
            onDismiss={handleNotificationDismiss}
          />
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Global Actions - Add Button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8">
              <Plus className="h-4 w-4" />
              <span className="sr-only">Add new</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={dropdownMode === 'customerForm' ? "w-80" : "w-56"}>
            {dropdownMode === 'menu' ? (
              <>
                <DropdownMenuLabel>Create New</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={handleNewAppointment}>
                    <Calendar className="mr-2 h-4 w-4" />
                    <span>New Appointment</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleCreateInvoice}>
                    <FileText className="mr-2 h-4 w-4" />
                    <span>Create Invoice</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleAddCustomer}
                    onSelect={(e) => e.preventDefault()}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    <span>Add Customer</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </>
            ) : (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-sm">Add Customer</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToMenu}
                    className="h-6 w-6 p-0"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                </div>

                <Form {...customerForm}>
                  <form onSubmit={customerForm.handleSubmit(handleCustomerSubmit)} className="space-y-3">
                    {/* Customer Name */}
                    <FormField
                      control={customerForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Customer Name</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <User className="absolute left-2 top-2.5 h-3 w-3 text-muted-foreground" />
                              <Input
                                {...field}
                                placeholder="Enter name"
                                disabled={isSubmitting}
                                className="pl-7 h-8 text-sm"
                                maxLength={100}
                              />
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    {/* Email */}
                    <FormField
                      control={customerForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Email</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Mail className="absolute left-2 top-2.5 h-3 w-3 text-muted-foreground" />
                              <Input
                                {...field}
                                type="email"
                                placeholder="customer@email.com"
                                disabled={isSubmitting}
                                className="pl-7 h-8 text-sm"
                              />
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    {/* Phone */}
                    <FormField
                      control={customerForm.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Phone</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Phone className="absolute left-2 top-2.5 h-3 w-3 text-muted-foreground" />
                              <Input
                                {...field}
                                type="tel"
                                placeholder="(555) 123-4567"
                                disabled={isSubmitting}
                                className="pl-7 h-8 text-sm"
                              />
                            </div>
                          </FormControl>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />

                    {/* Buttons */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleBackToMenu}
                        disabled={isSubmitting}
                        className="flex-1 h-8 text-xs"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting || !customerForm.formState.isValid}
                        className="flex-1 h-8 text-xs"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Check className="mr-1 h-3 w-3" />
                            Create
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications (placeholder for future) */}
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Bell className="h-4 w-4" />
          <span className="sr-only">Notifications</span>
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarImage src={currentUser?.avatar} alt={currentUser?.name} />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold">
                  {getUserInitials(currentUser?.name)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {currentUser?.name || 'User'}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {currentUser?.email}
                </p>
              </div>
            </DropdownMenuLabel>

            {/* Business Switcher - only show if user has multiple businesses */}
            {allBusinesses && allBusinesses.length > 1 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Switch Business
                </DropdownMenuLabel>
                {allBusinesses.map((business) => (
                  <DropdownMenuItem
                    key={business.id}
                    onClick={() => switchBusiness(business.id)}
                    disabled={switchingBusiness}
                    className={cn(
                      "cursor-pointer",
                      business.id === currentUser?.business?.id && "bg-accent"
                    )}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <span className="truncate">{business.name}</span>
                      </div>
                      {business.id === currentUser?.business?.id && (
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      )}
                      {switchingBusiness && business.id !== currentUser?.business?.id && (
                        <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate('/configuration/business')}>
                <Building2 className="mr-2 h-4 w-4" />
                <span>Business Settings</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/account/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Account Settings</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

      </div>
    </header>
  );
};

export default UniversalHeader;